import React, { useMemo } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Alert
} from "reactstrap";
import { FiDownload, FiInfo } from "react-icons/fi";
import { useTable } from "react-table";
import { useSelector } from "react-redux";
import { RootState } from "../../slices/store";

interface ColumnConfig {
    header: string;
    accessor: string;
    formatter?: (value: any) => string;
}

interface UnderlyingDataModalProps {
    isOpen: boolean;
    toggle: () => void;
    kpiTitle?: string;
    kpiData: Array<Record<string, any>>;
    formatValue?: (value: number, title: string) => string;
    dataBy?: string;
    columns: ColumnConfig[];
    title: string;
    hideInfoAlert?: boolean;
}

const UnderlyingdataModal: React.FC<UnderlyingDataModalProps> = ({
    isOpen,
    toggle,
    kpiData,
    dataBy = 'day',
    columns,
    title,
    hideInfoAlert = false
}) => {

    // Get campaign name from Redux reports state
    const reports = useSelector((state: RootState) => state.reports);
    const campaignName = reports.selectedReportOrder?.name || '';

    // Define table columns using provided configuration
    const tableColumns = useMemo(() => {
        return columns.map(col => ({
            Header: col.header,
            accessor: col.accessor,
            Cell: ({ value }: { value: any }) => {
                if (col.formatter) {
                    return col.formatter(value);
                }
                return value;
            }
        }));
    }, [columns]);

    // Initialize react-table
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable(
        {
            columns: tableColumns,
            data: kpiData || [],
        }
    );

    const downloadCSV = () => {
        if (!kpiData || kpiData.length === 0) return;

        // Helper function to escape CSV values (wraped in quotes to handle data containing comma, quote, or newline)
        const escapeCSVValue = (value: string): string => {
            if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
        };

        const campaignRow = campaignName ? `Campaign: ${escapeCSVValue(campaignName)}\n\n` : '';

        // Use columns configuration for CSV export
        const headers = columns.map(col => col.header);
        const csvRows = [
            headers.map(escapeCSVValue).join(','),
            ...kpiData.map((item: any) => {
                return columns.map(col => {
                    const value = item[col.accessor];
                    const formattedValue = col.formatter ? col.formatter(value) : String(value);
                    return escapeCSVValue(formattedValue);
                }).join(',');
            })
        ];

        const csvContent = campaignRow + csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        // Generate filename
        const sanitize = (str: string) => str.replace(/[^a-z0-9]/gi, '_');
        const filename = campaignName
            ? `${sanitize(campaignName)}_${sanitize(title)}_underlying_data.csv`
            : `${sanitize(title)}_underlying_data.csv`;

        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
            <ModalHeader toggle={toggle}>
                {title} - Underlying Data
            </ModalHeader>
            <ModalBody>
                {/* Info Alert - conditionally rendered */}
                {!hideInfoAlert && (
                    <Alert color="info" className="d-flex align-items-center mb-3">
                        {(FiInfo as unknown as React.FC<any>)({ size: 18, className: 'me-2' })}
                        <span>Aggregate data is not available while viewing underlying data</span>
                    </Alert>
                )}

                {/* Table Container with Fixed Header */}
                <div style={{
                    maxHeight: '380px',
                    overflowY: 'auto',
                    borderRadius: '4px',
                    position: 'relative'
                }}>
                    <table {...getTableProps()} className="table table-bordered mb-0">
                        <thead style={{
                            position: 'sticky',
                            top: 0,
                            backgroundColor: '#fff',
                            zIndex: 10
                        }}>
                            {headerGroups.map((headerGroup: any, idx: number) => (
                                <tr {...headerGroup.getHeaderGroupProps()} key={`header-group-${idx}`}>
                                    {headerGroup.headers.map((column: any, colIdx: number) => (
                                        <th {...column.getHeaderProps()} style={{ padding: '12px' }} key={`header-${colIdx}`}>
                                            {column.render('Header')}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {rows.length > 0 ? (
                                rows.map((row: any, rowIdx: number) => {
                                    prepareRow(row);
                                    return (
                                        <tr {...row.getRowProps()} key={`row-${rowIdx}`}>
                                            {row.cells.map((cell: any, cellIdx: number) => (
                                                <td {...cell.getCellProps()} style={{ padding: '12px' }} key={`cell-${rowIdx}-${cellIdx}`}>
                                                    {cell.render('Cell')}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={tableColumns.length} className="text-center text-muted" style={{ padding: '20px' }}>
                                        No data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Download CSV Button */}
                <div className="d-flex justify-content-end mt-3">
                    <button className="btn btn-primary button-primary"
                        onClick={downloadCSV}
                        disabled={!kpiData || kpiData.length === 0}
                    >
                        {(FiDownload as unknown as React.FC<any>)({ size: 16 })}
                        <span>Download CSV</span>
                    </button>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default UnderlyingdataModal;
